import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Calendar, AlertTriangle, Star, MessageSquare, Mail, Brain, Layout, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  actionBadge, actionAccent, confidenceBadge, severityStyle, collectAllTickers, chipStyle,
} from './reportHelpers';

/**
 * Full-page detail view for a single daily report.
 *
 * Two render modes:
 *   - "structured" (default): the rich React layout below.
 *   - "email":     the cached HTML the recipient saw, displayed verbatim in
 *                  a sandboxed iframe. Toggle is hidden if report.emailHtml
 *                  is empty (the report was never sent or the field is null
 *                  on legacy migrated docs).
 */
const ReportDetail = ({ report, onBack }) => {
  const [view, setView] = useState('structured');

  if (!report) {
    return (
      <div>
        <button className="btn btn-secondary" onClick={onBack}>
          <ArrowLeft size={16} /> Back to list
        </button>
        <div className="empty-state" style={{ marginTop: '2rem' }}>
          <p>Report not found</p>
        </div>
      </div>
    );
  }

  const so = report.structuredOutput || {};
  const themes = so.themes || [];
  const callsToAction = so.callsToAction || [];
  const portfolioAlerts = so.portfolioAlerts || [];
  const tickerHighlights = so.tickerHighlights || [];
  const summary = so.summary || '';

  const date = parseDate(report.reportDate || report.generatedAt);
  const generatedAt = parseDate(report.generatedAt);
  const sentAt = parseDate(report.sentAt);
  const hasEmailHtml = typeof report.emailHtml === 'string' && report.emailHtml.length > 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <button
          className="btn btn-secondary"
          onClick={onBack}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
        >
          <ArrowLeft size={16} /> Back to list
        </button>

        {hasEmailHtml && (
          <div className="filter-buttons" style={{ margin: 0 }}>
            <button
              className={`btn ${view === 'structured' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setView('structured')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
            >
              <Layout size={16} /> Structured
            </button>
            <button
              className={`btn ${view === 'email' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setView('email')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
            >
              <Mail size={16} /> Email
            </button>
          </div>
        )}
      </div>

      {view === 'email' && hasEmailHtml && (
        <EmailFrame html={report.emailHtml} />
      )}
      {view === 'structured' && (
        <StructuredView
          report={report}
          date={date}
          generatedAt={generatedAt}
          sentAt={sentAt}
          summary={summary}
          themes={themes}
          callsToAction={callsToAction}
          portfolioAlerts={portfolioAlerts}
          tickerHighlights={tickerHighlights}
        />
      )}
    </div>
  );
};

/**
 * Renders the cached email HTML in a sandboxed iframe sized to its content.
 * sandbox="allow-same-origin" without allow-scripts drops script execution
 * (the email HTML doesn't need any) while keeping the iframe same-origin
 * so we can measure scrollHeight to auto-size the frame. srcDoc avoids
 * a blob URL.
 */
const EmailFrame = ({ html }) => {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(600);

  useEffect(() => {
    // Auto-size to the iframe's body height when content loads. Same-origin
    // because srcDoc-loaded iframes share the parent's origin (they're not
    // cross-origin), so we can read scrollHeight directly.
    const iframe = iframeRef.current;
    if (!iframe) return;

    const onLoad = () => {
      try {
        const doc = iframe.contentDocument;
        if (doc && doc.body) {
          const measured = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
          if (measured > 0) setHeight(measured + 24); // small padding
        }
      } catch (e) {
        // Defensive -- some browsers may block the read; default 600px is fine.
        console.warn('Could not measure email iframe height', e);
      }
    };

    iframe.addEventListener('load', onLoad);
    return () => iframe.removeEventListener('load', onLoad);
  }, [html]);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <iframe
        ref={iframeRef}
        title="Email render"
        srcDoc={html}
        sandbox="allow-same-origin"
        style={{
          width: '100%',
          height: `${height}px`,
          border: 'none',
          display: 'block',
        }}
      />
    </div>
  );
};

const StructuredView = ({ report, date, generatedAt, sentAt, summary, themes, callsToAction, portfolioAlerts, tickerHighlights }) => {
  const navigate = useNavigate();
  const tickerLink = (symbol) =>
    symbol ? `/mentions/tickers/${encodeURIComponent(symbol)}` : null;
  return (
    <>

      {/* Hero */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          <Brain size={16} />
          <span>Daily Investment Report</span>
        </div>
        <h1 style={{ margin: 0, marginBottom: '0.75rem', lineHeight: 1.3 }}>
          {date ? format(date, 'EEEE, MMMM dd, yyyy') : 'Daily Report'}
        </h1>
        {summary && (
          <p style={{ fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--text-primary)', marginTop: 0 }}>
            {summary}
          </p>
        )}

        {/* Ticker chip strip -- every unique ticker mentioned in calls/alerts/highlights */}
        <TickerChipStrip report={report} />

        {/* Stats strip */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.5rem',
          marginTop: '1.25rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--border-color)',
          fontSize: '0.875rem',
          color: 'var(--text-secondary)',
        }}>
          {report.messageCount > 0 && (
            <Stat icon={<MessageSquare size={14} />} label="Messages" value={report.messageCount} />
          )}
          <Stat label="Themes" value={themes.length} />
          <Stat label="Actions" value={callsToAction.length} />
          {portfolioAlerts.length > 0 && <Stat label="Alerts" value={portfolioAlerts.length} />}
          {tickerHighlights.length > 0 && <Stat label="Highlights" value={tickerHighlights.length} />}
          {generatedAt && <Stat icon={<Calendar size={14} />} label="Generated" value={format(generatedAt, 'MMM dd, h:mm a')} />}
          {sentAt && <Stat icon={<Mail size={14} />} label="Emailed" value={format(sentAt, 'MMM dd, h:mm a')} />}
        </div>
      </div>

      {/* Themes */}
      {themes.length > 0 && (
        <Section title="Today's Focus">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {themes.map((t, i) => (
              <div
                key={i}
                style={{
                  padding: '1rem',
                  // Use --bg-tertiary so the inner card is *elevated* against
                  // the parent .card in both themes. --bg-secondary in dark
                  // mode is darker than --bg-primary, which would invert the
                  // visual hierarchy.
                  background: 'var(--bg-tertiary)',
                  borderRadius: '6px',
                  borderLeft: '3px solid var(--primary-color)',
                }}
              >
                <div
                  role="link"
                  tabIndex={0}
                  style={{ fontWeight: 600, color: 'var(--primary-color)', marginBottom: '0.375rem', cursor: 'pointer' }}
                  onClick={() => navigate(`/mentions/themes/${encodeURIComponent(toThemeSlug(t.title))}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/mentions/themes/${encodeURIComponent(toThemeSlug(t.title))}`);
                    }
                  }}
                >
                  {t.title}
                </div>
                {t.body && (
                  <div style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                    {t.body}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Calls to action */}
      {callsToAction.length > 0 && (
        <Section title="Actionable Recommendations">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {[...callsToAction]
              .sort((a, b) => (a.priority ?? 5) - (b.priority ?? 5))
              .map((c, i) => (
                <div
                  key={i}
                  style={{
                    padding: '1rem',
                    // Theme-aware. Previously 'white', which broke dark mode --
                    // forced a white card while child text inherited the
                    // dark-mode --text-primary, leaving the card unreadable.
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    borderLeft: `4px solid ${actionAccent(c.action)}`,
                    borderRadius: '6px',
                    color: 'var(--text-primary)',
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span className={`badge ${actionBadge(c.action)}`}>{c.action}</span>
                    {c.ticker && (
                      <span
                        role="link"
                        tabIndex={0}
                        style={{ fontWeight: 700, color: 'var(--primary-color)', fontSize: '1.125rem', cursor: 'pointer' }}
                        onClick={() => navigate(tickerLink(c.ticker))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(tickerLink(c.ticker));
                          }
                        }}
                      >
                        {c.ticker}
                      </span>
                    )}
                    {c.confidence && (
                      <span className={`badge ${confidenceBadge(c.confidence)}`}>{c.confidence}</span>
                    )}
                    {c.priority != null && (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                        Priority {c.priority}
                      </span>
                    )}
                    {c.targetPrice != null && (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                        Target ${Number(c.targetPrice).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {c.rationale && (
                    <p style={{ margin: 0, fontSize: '0.9375rem', lineHeight: 1.55 }}>{c.rationale}</p>
                  )}
                </div>
              ))}
          </div>
        </Section>
      )}

      {/* Portfolio alerts */}
      {portfolioAlerts.length > 0 && (
        <Section title="Portfolio Alerts" icon={<AlertTriangle size={16} />}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {portfolioAlerts.map((a, i) => (
              <div
                key={i}
                style={{
                  ...severityStyle(a.severity),
                  padding: '0.75rem 1rem',
                  borderRadius: '4px',
                  fontSize: '0.9375rem',
                  lineHeight: 1.5,
                }}
              >
                {a.ticker && (
                  <strong
                    role="link"
                    tabIndex={0}
                    style={{ color: 'var(--primary-color)', cursor: 'pointer' }}
                    onClick={() => navigate(tickerLink(a.ticker))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(tickerLink(a.ticker));
                      }
                    }}
                  >
                    {a.ticker}:{' '}
                  </strong>
                )}
                <span>{a.message}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Ticker highlights */}
      {tickerHighlights.length > 0 && (
        <Section title="Ticker Highlights" icon={<Star size={16} />}>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {tickerHighlights.map((h, i) => (
              <li key={i} style={{ fontSize: '0.9375rem', lineHeight: 1.55 }}>
                <strong
                  role="link"
                  tabIndex={0}
                  style={{ color: 'var(--primary-color)', cursor: 'pointer' }}
                  onClick={() => navigate(tickerLink(h.ticker))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(tickerLink(h.ticker));
                    }
                  }}
                >
                  {h.ticker}
                </strong>: {h.note}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Empty-state when nothing parsed -- migrated legacy docs */}
      {themes.length === 0 && callsToAction.length === 0 &&
       portfolioAlerts.length === 0 && tickerHighlights.length === 0 &&
       !summary && (
        <div className="empty-state">
          <p>This report has no structured content. It may be a legacy migrated doc.</p>
        </div>
      )}
    </>
  );
};

// Helpers ---------------------------------------------------------
// (action/severity/confidence helpers live in reportHelpers.js for reuse
//  across Analysis list, Dashboard recent rows, and this detail view.)

/**
 * Lossy slug derivation for theme titles. Mirrors the backend's
 * MentionAggregator.ToSlug -- lowercase, alphanumerics + hyphens only.
 * The DailyReportTheme structured output stores titles, not slugs;
 * derive client-side so theme tags can deep-link into /mentions/themes/<slug>.
 * The Mentions page's lookup does fuzzy slug matching, so minor differences
 * still resolve.
 */
const toThemeSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const parseDate = (raw) => {
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? parseISO(raw) : new Date(raw);
  } catch {
    return null;
  }
};

// Subcomponents ---------------------------------------------------

const Section = ({ title, icon, children }) => (
  <div className="card" style={{ marginBottom: '1.25rem' }}>
    <h2 style={{
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--text-secondary)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginTop: 0,
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
    }}>
      {icon}
      {title}
    </h2>
    {children}
  </div>
);

const Stat = ({ icon, label, value }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
    {icon}
    <span style={{ color: 'var(--text-secondary)' }}>{label}:</span>
    <strong style={{ color: 'var(--text-primary)' }}>{value}</strong>
  </div>
);

/**
 * Compact strip of every unique ticker mentioned in this report (across
 * callsToAction, portfolioAlerts, tickerHighlights). Quick at-a-glance
 * "what does this report touch" without scrolling. Hidden if zero
 * tickers are present.
 */
const TickerChipStrip = ({ report }) => {
  const navigate = useNavigate();
  const tickers = collectAllTickers(report);
  if (tickers.length === 0) return null;
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.375rem',
      alignItems: 'center',
      marginTop: '1rem',
    }}>
      <Tag size={14} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
      {tickers.map((t) => (
        <span
          key={t}
          role="link"
          tabIndex={0}
          style={{ ...chipStyle('blue'), fontSize: '0.8125rem', letterSpacing: '0.025em', cursor: 'pointer' }}
          onClick={() => navigate(`/mentions/tickers/${encodeURIComponent(t)}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/mentions/tickers/${encodeURIComponent(t)}`);
            }
          }}
        >
          {t}
        </span>
      ))}
    </div>
  );
};

export default ReportDetail;
